import { Link, useParams } from "react-router-dom";
import SiteShell from "@/components/feature/SiteShell";
import Breadcrumb from "@/components/base/Breadcrumb";
import { getTitleById } from "@/hooks/useCatalog";
import DetailHero from "./components/DetailHero";
import RelatedRail from "./components/RelatedRail";

export default function DetailPage() {
  const { id } = useParams();
  const item = getTitleById(id);

  if (!item) {
    return (
      <SiteShell
        breadcrumb="/detail"
        pageTitle="影视作品不存在 - 长日光阴"
        pageDescription="该影视作品不存在或已下架，你可以返回长日光阴首页继续浏览最新电影与电视剧推荐。"
        pageKeywords="长日光阴,影视详情"
      >
        <div className="flex min-h-[75vh] flex-col items-center justify-center px-4 pt-24 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-2xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">该影视作品不存在或已下架</h1>
          <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-600">
            链接可能已失效，或者这部作品已从资源库中移除。可以去分类页看看其他内容。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="rounded-md bg-primary-800 px-5 py-2.5 text-sm text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-700"
            >
              返回首页
            </Link>
            <Link
              to="/category/movie"
              className="rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 whitespace-nowrap cursor-pointer hover:border-primary-300 hover:text-primary-700"
            >
              浏览电影分类
            </Link>
          </div>
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell
      breadcrumb={`/detail/${item.id}`}
      pageTitle={`${item.title} - 影视详情 - 长日光阴`}
      pageDescription={`${item.title}（${item.year}，${item.region}）${item.genres.join("、")}作品，导演${item.director}。${item.tagline}长日光阴为你整理作品简介、主创信息与同类型推荐。`}
      pageKeywords={`${item.title},长日光阴,影视详情,${item.genres.join(",")},${item.region}`}
    >
      <div className="pt-16 md:pt-[72px]">
        <div className="w-full bg-primary-950 px-4 pt-4 md:px-8">
          <div className="mx-auto max-w-[1440px]">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-xs text-primary-200">
              <Link to="/" className="transition-colors cursor-pointer hover:text-secondary-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <Link
                to={`/category/${item.type}`}
                className="transition-colors cursor-pointer hover:text-secondary-400"
              >
                {item.type === "movie" ? "电影" : "电视剧"}
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-background-100">{item.title}</span>
            </nav>
          </div>
        </div>

        <DetailHero item={item} />

        <section className="w-full px-4 md:px-8">
          <div className="mx-auto max-w-[1440px] border-t border-background-200 py-8">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {[
                {
                  icon: "ri-book-open-line",
                  title: "作品简介",
                  desc: item.synopsis.slice(0, 56),
                },
                {
                  icon: "ri-user-star-line",
                  title: "主创阵容",
                  desc: `导演 ${item.director}，主演 ${item.cast.slice(0, 2).join("、")} 等。`,
                },
                {
                  icon: "ri-bookmark-3-line",
                  title: "观看提示",
                  desc: "本站仅提供影视资料与推荐，不提供在线播放与下载。",
                },
              ].map((card) => (
                <div key={card.title} className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${card.icon} text-base`} aria-hidden="true"></i>
                    </span>
                    <h2 className="text-sm font-medium text-foreground-900">{card.title}</h2>
                  </div>
                  <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-foreground-600">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="border-t border-background-200 bg-background-100">
          <RelatedRail item={item} />
        </div>

        <div className="w-full px-4 md:px-8 py-10">
          <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-4 rounded-lg bg-primary-900 px-5 py-6 md:flex-row md:items-center md:px-8">
            <div>
              <Breadcrumb
                items={[
                  { label: "首页", to: "/" },
                  { label: item.type === "movie" ? "电影" : "电视剧", to: `/category/${item.type}` },
                  { label: item.title },
                ]}
              />
              <p className="mt-3 font-heading text-lg font-bold text-background-50">
                还没找到想看的？去{" "}
                <Link to={`/category/${item.type}/list`} className="text-secondary-400 underline-offset-4 hover:underline cursor-pointer">
                  完整{item.type === "movie" ? "电影" : "剧集"}列表
                </Link>{" "}
                看看
              </p>
            </div>
            <Link
              to={`/category/${item.type}/list`}
              className="inline-flex items-center gap-2 rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-primary-950 whitespace-nowrap transition-colors cursor-pointer hover:bg-secondary-400"
            >
              打开筛选
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </div>
    </SiteShell>
  );
}